import { useEffect, useRef, useState } from "react";
import { ICON_BASE_PATH, COUNTRY_BASE_PATH } from "../../../../utils/config";
import { useTranslation } from "react-i18next";
import {
  fetchCountries,
  type Country,
} from "@/app/services/localservices/countryService";

interface BeneficiaryBankCountryProps {
  countries: string[];
  label?: string;
  value: string;
  onChange: (value: string) => void;
  validationMessage?: string;
  starlable?: boolean;
  disable?: boolean;
  fieldName?: string;
  triggerValidation?: boolean;
  onValidate?: (field: string, isValid: boolean) => void;
}

const getFlagUrl = (country: Country): string => {
  if (country.flagIcon) {
    if (
      country.flagIcon.startsWith("http://") ||
      country.flagIcon.startsWith("https://") ||
      country.flagIcon.startsWith("/")
    ) {
      return country.flagIcon;
    }
    return `${COUNTRY_BASE_PATH}${country.flagIcon}`;
  }
  return "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
};

const BeneficiaryBankCountry: React.FC<BeneficiaryBankCountryProps> = ({
  countries,
  label = "select country",
  value,
  onChange,
  validationMessage,
  starlable = true,
  disable = false,
  fieldName = "beneficiaryBankCountry",
  triggerValidation = false,
  onValidate,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [countryDataList, setCountryDataList] = useState<Country[]>([]);

  const suggestionClickedRef = useRef(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Load country data for flag matching
  useEffect(() => {
    fetchCountries()
      .then(setCountryDataList)
      .catch(() => setCountryDataList([]));
  }, []);

  // Match countryName → Country object for flag
  const getCountryDataByName = (name: string): Country | undefined => {
    return countryDataList.find(
      (c) => c.name.toLowerCase() === name.toLowerCase(),
    );
  };

  const selectedCountryData = value ? getCountryDataByName(value) : undefined;

  const validate = (val: string): string => {
    if (!val) return `${t("please_select_the")} ${label}.`;
    if (touched && val && !countries.includes(val)) {
      const hasPartialMatch = countries.some((country) =>
        country.toLowerCase().startsWith(val.toLowerCase()),
      );
      if (!hasPartialMatch) {
        return `${t("please_select_valid")} ${label}.`;
      }
    }
    return "";
  };

  const updateValidation = (val: string) => {
    const newError = validate(val);
    setError(newError);
    const isValid = countries.includes(val);
    onValidate?.(fieldName, isValid);
    return newError;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    onChange(newVal);
    setActiveIndex(-1);

    const filtered = newVal
      ? countries.filter((c) =>
          c.toLowerCase().startsWith(newVal.toLowerCase()),
        )
      : countries;
    setFilteredCountries(filtered);
    setShowSuggestions(true);

    if (touched && error) {
      const hasPartialMatch = filtered.length > 0;
      if (hasPartialMatch || !newVal) setError("");
      else setError(validate(newVal));
    }
  };

  const handleSelect = (countryName: string) => {
    suggestionClickedRef.current = true;
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    onChange(countryName);
    setShowSuggestions(false);
    setActiveIndex(-1);
    setError("");
    setTouched(true);
    onValidate?.(fieldName, true);
    setTimeout(() => {
      suggestionClickedRef.current = false;
    }, 100);
  };

  const handleFocus = () => {
    const filtered = value
      ? countries.filter((c) => c.toLowerCase().startsWith(value.toLowerCase()))
      : countries;
    setFilteredCountries(filtered);
    setShowSuggestions(true);
  };

  const handleBlur = () => {
    if (suggestionClickedRef.current) return;
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => {
      setShowSuggestions(false);
      setActiveIndex(-1);
      setTouched(true);
      updateValidation(value);
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredCountries.length === 0) return;
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = Math.min(activeIndex + 1, filteredCountries.length - 1);
        setActiveIndex(next);
        listRef.current
          ?.querySelectorAll<HTMLDivElement>("[data-item]")
          [next]?.scrollIntoView({ block: "nearest" });
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = Math.max(activeIndex - 1, 0);
        setActiveIndex(prev);
        listRef.current
          ?.querySelectorAll<HTMLDivElement>("[data-item]")
          [prev]?.scrollIntoView({ block: "nearest" });
        break;
      }
      case "Enter": {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < filteredCountries.length) {
          handleSelect(filteredCountries[activeIndex]);
        } else if (filteredCountries.length === 1) {
          handleSelect(filteredCountries[0]);
        } else {
          setShowSuggestions(false);
        }
        break;
      }
      case "Escape":
        setShowSuggestions(false);
        setActiveIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      updateValidation(value);
    }
  }, [triggerValidation]);

  useEffect(() => {
    if (validationMessage) {
      setError(validationMessage);
      setTouched(true);
      onValidate?.(fieldName, false);
    } else if (touched) {
      updateValidation(value);
    }
  }, [validationMessage]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const isInvalid = !!error && touched;

  return (
    <div className="gx_input_container">
      <label
        className={`input-label gx-font-semibold ${value ? "floating" : ""}`}
      >
        {t(label)}
        {starlable && (
          <span
            className={`gx_input_star ${
              value ? "gx_input_star_float" : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}
      </label>

      <div className={`gx_input_cont ${isInvalid ? "invalid" : "valid"}`}>
        {/* Static icon */}
        {/* {!selectedCountryData && (
          <img
            src={`${ICON_BASE_PATH}beneficiarybankcountry.svg`}
            alt="beneficiarybankcountry"
          />
        )} */}

        {/* Flag shown when country is selected */}
        <div className="gx-country-flag_name">
          <img
            src={`${ICON_BASE_PATH}beneficiarybankcountry.svg`}
            alt="beneficiarybankcountry"
          />

          {selectedCountryData && (
            <img
              src={getFlagUrl(selectedCountryData)}
              alt={selectedCountryData.name}
              className="gx-country-flag"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}

          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disable}
            placeholder=""
            className={`gx-textbox-css ${isInvalid ? "gx-invalid" : "gx-valid"}`}
            aria-invalid={isInvalid}
          />

          {value && (
            <button
              type="button"
              aria-label="Clear selection"
              onClick={() => onChange("")}
              className="gx_clear_btn"
            >
              <img src={`${ICON_BASE_PATH}cross.svg`} alt="Clear" />
            </button>
          )}
        </div>

        {/* Dropdown suggestions */}
        {showSuggestions && filteredCountries.length > 0 && (
          <div className="gx-bene-currency-filter" ref={listRef}>
            {filteredCountries.map((countryName, index) => {
              const countryData = getCountryDataByName(countryName);
              return (
                <div
                  key={countryName}
                  data-item
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(countryName);
                  }}
                  className={`gx-country-item gx-country-row ${
                    index === activeIndex ? "bg-blue-50" : "hover:bg-gray-100"
                  }`}
                >
                  {countryData ? (
                    <img
                      src={getFlagUrl(countryData)}
                      alt={countryName}
                      className="gx-flag-img"
                      onError={(e) => {
                        e.currentTarget.style.visibility = "hidden";
                      }}
                    />
                  ) : (
                    <span className="gx-flag-placeholder" />
                  )}
                  <span className="gx-p12-m">{countryName}</span>
                </div>
              );
            })}
          </div>
        )}

        {showSuggestions &&
          filteredCountries.length === 0 &&
          value.length > 0 && (
            <div className="gx-bene-currency-no_match">
              <p className="text-sm text-gray-500">
                No matching countries found
              </p>
            </div>
          )}
      </div>

      {isInvalid && (
        <div className="gx_input_validationmessage gx-font-regular">
          {error}
        </div>
      )}
    </div>
  );
};

export default BeneficiaryBankCountry;
